import { ActivityReply } from "../@types/activities.ts";
import queryClient from "../helpers/queryClient.tsx";
import { filter, includes, map } from "lodash";
import { useUpdateActivityInCache } from "./activitystreams/useUpdateActivityInCache.ts";

export function useUpdateActivityReplyInCache() {
  const { updateActivity } = useUpdateActivityInCache();

  function updateReply(
    activityId?: string,
    replyId?: string,
    type: "replies" | "answers" = "replies",
    updater?: (reply: ActivityReply) => ActivityReply,
  ) {
    if (!activityId || !updater || !replyId) return;

    // "has the query been fetched" (undefined = never), consistent with
    // addReply — an empty list is a valid cache state
    const hasCachedReplies =
      queryClient.getQueryData(`/activities/${activityId}/${type}`) !==
      undefined;

    hasCachedReplies &&
      queryClient.setQueriesData(
        { queryKey: `/activities/${activityId}/${type}` },
        (replies) =>
          map(replies as ActivityReply[], (reply: ActivityReply) =>
            reply.id === replyId ? updater(reply) : reply,
          ),
      );

    updateActivity(activityId, (activity) =>
      includes(map(activity.object[type], "id"), replyId)
        ? {
            ...activity,
            object: {
              ...activity.object,
              [type]: map(activity.object[type], (reply: ActivityReply) =>
                reply.id === replyId ? updater(reply) : reply,
              ),
            },
          }
        : activity,
    );
  }

  function addReply(
    activityId: string,
    type: "replies" | "answers" = "replies",
    reply: ActivityReply,
  ) {
    // an already fetched but EMPTY list must receive the insert too —
    // isEmpty([]) would skip it and the new reply only appeared after a
    // refetch (e.g. reopening the app's replies bottom sheet)
    const hasCachedReplies =
      queryClient.getQueryData(`/activities/${activityId}/${type}`) !==
      undefined;

    hasCachedReplies &&
      queryClient.setQueriesData(
        { queryKey: `/activities/${activityId}/${type}` },
        (replies) => [...(replies as ActivityReply[]), reply],
      );

    const countType = type === "replies" ? "reply_count" : "answer_count";

    updateActivity(activityId, (activity) => ({
      ...activity,
      object: {
        ...activity.object,
        [countType]: activity.object[countType] + 1,
        [type]: [...activity.object[type], reply],
      },
    }));
  }

  function removeReply(
    activityId: string,
    replyId: string,
    type: "replies" | "answers" = "replies",
  ) {
    const hasCachedReplies =
      queryClient.getQueryData(`/activities/${activityId}/${type}`) !==
      undefined;

    hasCachedReplies &&
      queryClient.setQueriesData(
        { queryKey: `/activities/${activityId}/${type}` },
        (replies) =>
          filter(replies as ActivityReply[], (reply) => reply.id !== replyId),
      );

    const countType = type === "replies" ? "reply_count" : "answer_count";

    updateActivity(activityId, (activity) => ({
      ...activity,
      object: {
        ...activity.object,
        [countType]: activity.object[countType] - 1,
        [type]: filter(activity.object[type], (reply) => reply.id !== replyId),
      },
    }));
  }

  return { updateReply, addReply, removeReply };
}
